/**
 * What a Roll button asks for (issue #19, Rolls): the page turns a request into a roll plan
 * (src/ui/roll-spec.ts) from the derived sheet, rolls it, and shows the result panel
 * (src/ui/roll-panel.tsx). Requests are plain data, so enriched descriptions can carry them.
 */
export type RollRequest =
  | {
      kind: "check";
      /** Foundry's inline check type: a skill slug, a save, "perception", "flat", a lore, a special statistic. */
      type: string;
      /** The sheet statistic's slug when the Character has one for the type; null for a flat check or an unknown statistic. */
      statistic: string | null;
      label: string;
      /** The Character's modifier; 0 for a flat check; null when the statistic is unknown. */
      modifier: number | null;
      /** The DC, resolved; null when none is given or it could not be resolved. */
      dc: number | null;
      /** The target's statistic the check is rolled against (`against:ac`), when the DC is theirs. */
      against: string | null;
      basic: boolean;
      traits: string[];
      options: string[];
      name: string | null;
    }
  | {
      kind: "damage";
      /** The formula with the Character's numbers filled in, in words ("3d6 fire, 1d4 cold"). */
      formula: string;
      /** Foundry's formula as written, `@` terms resolved ("{3d6[fire],1d4[cold]}"); what is rolled. */
      raw?: string;
      label: string;
      traits: string[];
      options: string[];
      name: string | null;
      /** `@` terms that could not be resolved from the sheet; the formula still shows them. */
      unresolved: string[];
    }
  | {
      kind: "inline";
      /** `[[/r 1d20+5]]`: the formula as written. */
      formula: string;
      label: string;
      flavor: string | null;
      /** r public, gmr GM only, br blind, sr self. */
      mode: "r" | "gmr" | "br" | "sr";
    }
  | {
      kind: "strike";
      strikeKey: string;
      label: string;
      /** Which Multiple Attack Penalty variant: 0, 1 or 2 attacks already made. */
      map: 0 | 1 | 2;
      modifier: number;
    }
  | {
      kind: "strike-damage";
      strikeKey: string;
      label: string;
      formula: string;
      critical: string | null;
      /** Roll the critical formula (a strike that critically hit). */
      asCritical?: boolean;
    }
  | {
      kind: "spell-attack";
      entryId: string;
      spellUuid: string;
      label: string;
      map: 0 | 1 | 2;
    }
  | {
      kind: "spell-damage";
      entryId: string;
      spellUuid: string;
      label: string;
      asCritical?: boolean;
    }
  /** The recovery check while dying: a flat check against the Session's recovery DC, its outcome applied on commit. */
  | { kind: "recovery" }
  /** A persistent damage Condition's damage, rolled to be taken now. */
  | { kind: "persistent-damage"; conditionId: string }
  /** The flat check that ends a persistent damage Condition. */
  | { kind: "persistent-check"; conditionId: string }
  /** Fast healing or regeneration with a formula. */
  | { kind: "fast-healing"; formula: string; label: string };

/** How the button was used: a long press asks for the physical dice to be entered. */
export interface RollOptions {
  physical?: boolean;
}

/** A request's label for notices and the Roll Log. */
export function requestLabel(request: RollRequest): string {
  switch (request.kind) {
    case "recovery": return "Recovery check";
    case "persistent-damage": return "Persistent damage";
    case "persistent-check": return "Persistent damage flat check";
    default: return request.label;
  }
}

/** What a d20 must show for each degree of success, from a modifier and a DC (Player Core: ±10 steps). */
export interface RollOutlook {
  criticalSuccess: number | null;
  success: number | null;
  failure: number | null;
  criticalFailure: number | null;
}

/**
 * The lowest d20 result that reaches each degree, before natural 20 and 1 adjustments; null when
 * the degree is out of reach on any die. Critical failure is the highest die that still critically fails.
 */
export function rollOutlook(modifier: number, dc: number): RollOutlook {
  const need = (target: number) => target - modifier;
  const clamp = (n: number): number | null => (n > 20 ? null : n < 1 ? 1 : n);
  const criticalSuccess = clamp(need(dc + 10));
  const success = clamp(need(dc));
  const failure = clamp(need(dc - 9));
  const criticalFailureMax = need(dc - 10);
  return {
    criticalSuccess,
    success,
    failure,
    criticalFailure: criticalFailureMax < 1 ? null : Math.min(20, criticalFailureMax),
  };
}
